import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient } from '@angular/common/http';
import { NgZone } from '@angular/core';
import firebase from "firebase/app";
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBar as ModernMatSnackBar } from '@angular/material/snack-bar';

export class ProjectManager {
    private static readonly SERVER = "https://us-central1-xbuild3d.cloudfunctions.net";

    static async saveProject(
        project: any,
        camera: any,
        customerid: string,
        projectid: string,
        afs: AngularFirestore
    ): Promise<void> {
        project.camera = JSON.stringify(camera.matrix.toArray());
        project.camerazoom = camera.zoom;
        project.lastsave = new Date().getTime();
        await afs.doc(`projects/userprojects/${customerid}/${projectid}`).update(JSON.parse(JSON.stringify(project)));
    }

    static async saveCells(
        customerid: string,
        projectid: string,
        sheetid: number,
        cellformulas: any,
        datasetids: any[],
        http: HttpClient,
        snackBar: MatSnackBar,
        zone: NgZone
    ): Promise<void> {
        console.log('saveCells', { customerid, projectid, sheets: [cellformulas], cells: cellformulas, datasetids });

        let attempt = 0;
        let r;
        while (attempt < 12) {
            try {
                r = await http.post(this.SERVER + "/applyCellUpdates", {
                    customerid,
                    projectid,
                    sheetid,
                    sheets: [cellformulas],
                    cells: cellformulas,
                    datasetids
                }).toPromise();
                if (r) break;
            } catch (e) {
                console.warn('Attempt ' + (attempt + 1) + ' failed', e);
            }
            attempt++;
            await new Promise(resolve => setTimeout(resolve, 400));
        }

        console.log('applyCellUpdates result', r);
        zone.run(() => {
            snackBar.open("Project saved", "", { duration: 1000 });
        });
    }

    static async cloneProject(
        project: any,
        newOwnerCustomerId: string,
        http: HttpClient,
        afs: AngularFirestore,
        snackbar: ModernMatSnackBar
    ): Promise<void> {
        const sourceProjectData = JSON.parse(JSON.stringify(project));
        const sourceProjectId = sourceProjectData.id;
        const isPublicExample = sourceProjectData.isPublicExample === true;
        const originalProjectOwnerId = sourceProjectData.userid;

        const newProjectDocument: any = { ...sourceProjectData };
        delete newProjectDocument.id;
        delete newProjectDocument.isPublicExample;

        newProjectDocument.name = (sourceProjectData.name || 'Unnamed Project') + "_copy_";
        let currentDate = new Date();
        newProjectDocument.name += currentDate.getFullYear() + "-" +
            ("0" + (currentDate.getMonth() + 1)).slice(-2) + "-" +
            ("0" + currentDate.getDate()).slice(-2) + " " +
            ("0" + currentDate.getHours()).slice(-2) + "-" +
            ("0" + currentDate.getMinutes()).slice(-2);
        newProjectDocument.createtime = new Date().getTime();
        newProjectDocument.lastsave = newProjectDocument.createtime;
        newProjectDocument.userid = newOwnerCustomerId;

        const newDocRef = await afs.collection(`projects/userprojects/${newOwnerCustomerId}`).add(newProjectDocument);
        const newProjectId = newDocRef.id;

        let sourceStylesPath: string;
        if (isPublicExample) {
            sourceStylesPath = `/projectspublic/${sourceProjectId}/tdstyles`;
        } else {
            if (!originalProjectOwnerId) {
                console.warn(`Source project (ID: ${sourceProjectId}) is missing 'userid'. Cannot determine original owner for styles path. Attempting to use newOwnerCustomerId as fallback for source path.`);
                sourceStylesPath = `/projects/userprojects/${newOwnerCustomerId}/${sourceProjectId}/tdstyles`;
            } else {
                sourceStylesPath = `/projects/userprojects/${originalProjectOwnerId}/${sourceProjectId}/tdstyles`;
            }
        }

        try {
            const tdStylesSnapshot = await afs.collection(sourceStylesPath).get().toPromise();

            if (tdStylesSnapshot.empty) {
                console.log(`No tdstyles found at ${sourceStylesPath} for project ID ${sourceProjectId}.`);
            } else {
                const batch = afs.firestore.batch();
                tdStylesSnapshot.docs.forEach(doc => {
                    const styleData = doc.data();
                    const newStyleDocRef = afs.doc(`/projects/userprojects/${newOwnerCustomerId}/${newProjectId}/tdstyles/${doc.id}`).ref;
                    batch.set(newStyleDocRef, styleData);
                });
                await batch.commit();
                console.log(`Successfully cloned ${tdStylesSnapshot.size} style documents from ${sourceStylesPath} to /projects/userprojects/${newOwnerCustomerId}/${newProjectId}/tdstyles`);
            }
        } catch (error) {
            console.warn(`Failed to clone tdstyles for project ${sourceProjectId} from ${sourceStylesPath}:`, error);
        }

        let attempt = 0;
        const maxAttempts = 7;
        const attemptInterval = 400;

        const cloneSheetsInternal = async () => {
            try {
                const response = await http.post(this.SERVER + "/copySheets", {
                    customerid: newOwnerCustomerId,
                    projectid: sourceProjectId,
                    newprojectid: newProjectId,
                    sheetscount: sourceProjectData.sheets?.length || 5,
                    isPublicExample: isPublicExample
                }).toPromise();
                console.log('clone sheets result', response);
                snackbar.open("Project duplicated. Go To Dashboard to load copy.", "", { duration: 3000 });
            } catch (error) {
                if (attempt < maxAttempts) {
                    setTimeout(() => {
                        attempt++;
                        cloneSheetsInternal();
                    }, attemptInterval);
                } else {
                    console.error('Failed to clone project sheets after ' + maxAttempts + ' attempts for new project ' + newProjectId, error);
                    snackbar.open("Failed to duplicate project sheets. Please try again.", "", { duration: 3000 });
                }
            }
        };

        await cloneSheetsInternal();
    }
}